import { useEffect, useRef, useState, type RefObject } from 'react';

import { abrirLeitorDeQr, type LeitorDeQuadros } from './leitor-de-qr';

/**
 * A CÂMERA QUE LÊ O ADESIVO, a mesma no celular e no computador.
 *
 * O laço saiu do leitor do celular (`mobile/componentes/LeitorDeQr`) quando a
 * mesa ganhou o leitor no campo da máquina (31/10/2026): os dois abrem a
 * câmera, leem dez quadros por segundo e param na primeira leitura que
 * preenche. Quem decide o que é "preencher" é a tela (`aoLer` devolve `true`
 * para parar); aqui só a câmera, o laço e o porquê de ela não ter aberto.
 */

/**
 * A câmera não abriu: o que houve e O QUE FAZER (p.61, "erro diz o que
 * fazer"). Cada causa tem a sua saída, e a saída comum a todas é a foto.
 *
 * - `NotAllowedError` / `SecurityError`: a pessoa (ou a política) negou. O
 *   navegador não pergunta de novo sozinho; a pessoa precisa liberar no
 *   cadeado ao lado do endereço.
 * - `NotFoundError` / `OverconstrainedError`: não há câmera (o computador de
 *   mesa sem webcam).
 * - `NotReadableError` / `AbortError`: a câmera existe e outro programa está
 *   com ela (a chamada de vídeo aberta).
 * - sem `mediaDevices`: o endereço não é seguro (`http://`), ou o navegador
 *   não tem câmera para página nenhuma.
 */
export function motivoDaCameraFechada(erro: unknown, temMediaDevices = true): string {
  if (!temMediaDevices) {
    return 'Este navegador não abre a câmera nesta página. Leia de uma foto do adesivo, ou use um leitor de código USB no campo da máquina.';
  }
  const nome = erro instanceof Error || (typeof erro === 'object' && erro !== null && 'name' in erro) ? String((erro as { name: unknown }).name) : '';
  if (nome === 'NotAllowedError' || nome === 'SecurityError' || nome === 'PermissionDeniedError') {
    return 'A câmera está bloqueada para o CT Frotas. Libere a câmera no cadeado ao lado do endereço do navegador e abra o leitor de novo, ou leia de uma foto do adesivo.';
  }
  if (nome === 'NotFoundError' || nome === 'OverconstrainedError' || nome === 'DevicesNotFoundError') {
    return 'Nenhuma câmera encontrada neste aparelho. Leia de uma foto do adesivo, ou use um leitor de código USB no campo da máquina.';
  }
  if (nome === 'NotReadableError' || nome === 'AbortError' || nome === 'TrackStartError') {
    return 'A câmera está em uso por outro programa. Feche a chamada de vídeo ou o programa que a usa e abra o leitor de novo, ou leia de uma foto do adesivo.';
  }
  return 'A câmera não abriu. Abra o leitor de novo, ou leia de uma foto do adesivo.';
}

export interface CameraDoLeitor {
  /** Vai no `<video>` da tela. */
  readonly video: RefObject<HTMLVideoElement | null>;
  /** A câmera ainda está abrindo (o navegador pode estar perguntando a permissão). */
  readonly abrindo: boolean;
  /** Por que a câmera não abriu, já com o que fazer; `null` com ela aberta ou abrindo. */
  readonly fechada: string | null;
}

/**
 * Abre a câmera traseira (no computador, a única que houver), lê dez quadros
 * por segundo e entrega cada texto lido a `aoLer`, que devolve `true` quando a
 * leitura preencheu e a câmera pode parar. `ligada = false` não abre nada.
 *
 * Dez por segundo, e não sessenta: o motor procura o código na imagem
 * inteira, e no ritmo do vídeo isso esquenta o aparelho e trava o vídeo, que é
 * o que faz a pessoa achar que o leitor não funciona.
 */
export function useCameraDoLeitor(aoLer: (texto: string) => boolean, ligada = true): CameraDoLeitor {
  const video = useRef<HTMLVideoElement | null>(null);
  const [fluxo, setFluxo] = useState<MediaStream | null>(null);
  const [fechada, setFechada] = useState<string | null>(null);
  /* `aoLer` numa referência: no efeito, reabriria a câmera a cada render. */
  const entregar = useRef(aoLer);
  entregar.current = aoLer;

  useEffect(() => {
    if (!ligada) return undefined;
    let ativo = true;
    let aberto: MediaStream | null = null;
    if (!navigator.mediaDevices?.getUserMedia) {
      setFechada(motivoDaCameraFechada(null, false));
      return undefined;
    }
    navigator.mediaDevices
      /* 1280 basta: o motor lê num quadro de 640, e pedir 4K só gastaria
         bateria para jogar pixel fora. */
      .getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 1280 } }, audio: false })
      .then((m) => {
        if (!ativo) {
          m.getTracks().forEach((t) => {
            t.stop();
          });
          return;
        }
        aberto = m;
        setFluxo(m);
      })
      .catch((erro: unknown) => {
        if (ativo) setFechada(motivoDaCameraFechada(erro));
      });
    return () => {
      ativo = false;
      aberto?.getTracks().forEach((t) => {
        t.stop();
      });
    };
  }, [ligada]);

  useEffect(() => {
    const v = video.current;
    if (v && fluxo) {
      v.srcObject = fluxo;
      void v.play().catch(() => undefined);
    }
  }, [fluxo]);

  /* O LAÇO DE LEITURA. Ele morre com o componente: um laço que sobrevive
     segura a câmera ligada e a luz acesa depois de a tela fechar. */
  useEffect(() => {
    if (fluxo === null) return undefined;
    let vivo = true;
    let relogio: ReturnType<typeof setTimeout> | null = null;
    let leitor: LeitorDeQuadros | null = null;

    const rodar = async () => {
      if (!vivo) return;
      const v = video.current;
      if (leitor !== null && v !== null && v.readyState >= 2) {
        const texto = await leitor.ler(v).catch(() => null);
        if (!vivo) return;
        if (texto !== null && entregar.current(texto)) {
          vivo = false;
          return;
        }
      }
      relogio = setTimeout(() => void rodar(), 100);
    };

    void abrirLeitorDeQr().then((l) => {
      if (!vivo) return;
      leitor = l;
      void rodar();
    });

    return () => {
      vivo = false;
      if (relogio !== null) clearTimeout(relogio);
    };
  }, [fluxo]);

  return { video, abrindo: ligada && fluxo === null && fechada === null, fechada };
}
